import { C, Note, P, Terms } from '../prose';
import type { Topic } from '../types';

/** Who may do what, how they get in, and how machines get in. */
export const konto = {
  rollen: {
    group: 'konto',
    title: { de: 'Rollen', en: 'Roles' },
    teaser: {
      de: 'Wer bearbeiten, wer veröffentlichen und wer nur lesen darf.',
      en: 'Who may edit, who may publish, and who may only read.',
    },
    body: {
      de: (
        <>
          <P>
            Die Trennung, auf die es ankommt, verläuft zwischen Bearbeiten und Veröffentlichen.
            Veröffentlichen ist der Moment, in dem eine Änderung echte Besucher erreicht — dass
            „Redakteur“ das nicht darf, ist der Zweck der Rolle.
          </P>
          <Terms
            items={[
              ['Inhaber', 'Darf alles, auch Team und Abrechnung.'],
              ['Administrator', 'Darf Websites und Konfiguration, aber keine Inhaber ernennen.'],
              ['Redakteur', 'Darf bearbeiten, aber nicht veröffentlichen.'],
              ['Auswertung', 'Darf lesen und auswerten, nichts ändern.'],
            ]}
          />
          <P>
            Eine Rolle gilt pro Konto, nicht pro Person: dieselbe Person kann Inhaber des eigenen
            Kontos und Redakteur beim Kunden sein. Mitglieder lassen sich zusätzlich auf einzelne
            Websites beschränken — nichts auszuwählen heißt alle, auch die, die es noch nicht gibt.
          </P>
          <Note>
            Der letzte Inhaber lässt sich nicht entfernen oder herabstufen. Danach könnte niemand
            mehr jemanden ernennen, und das Konto wäre nur noch über den Server zu reparieren.
          </Note>
        </>
      ),
      en: (
        <>
          <P>
            The separation that matters runs between editing and publishing. Publishing is the
            moment a change reaches real visitors — that "editor" cannot do it is the purpose of
            the role.
          </P>
          <Terms
            items={[
              ['Owner', 'May do everything, including team and billing.'],
              ['Administrator', 'May manage websites and configuration, but cannot appoint owners.'],
              ['Editor', 'May edit, but not publish.'],
              ['Analyst', 'May read and analyse, and change nothing.'],
            ]}
          />
          <P>
            A role applies per account, not per person: the same person can be owner of their own
            account and editor on a client's. Members can additionally be restricted to individual
            websites — selecting none means all of them, including ones that do not exist yet.
          </P>
          <Note>
            The last owner cannot be removed or demoted. After that nobody could appoint anyone,
            and the account would only be repairable from the server.
          </Note>
        </>
      ),
    },
  },

  einladungen: {
    group: 'konto',
    title: { de: 'Einladungen', en: 'Invitations' },
    teaser: {
      de: 'Wie jemand dazukommt, und warum der Link nur einmal zu sehen ist.',
      en: 'How someone joins, and why the link is shown only once.',
    },
    body: {
      de: (
        <>
          <P>
            Eine Einladung erzeugt einen Link, der sieben Tage gültig ist. Ist kein Mailrelay
            eingerichtet, wird er hier angezeigt statt versendet — einmal, zum Kopieren. Ein Link,
            der nur noch in einem Log steht, das niemand liest, wäre schlechter als einer, der
            weitergegeben werden muss.
          </P>
          <P>
            Vergeben lassen sich nur Rollen unterhalb der eigenen: ein Administrator kann
            Redakteure und Auswertung einladen, keine weiteren Administratoren.
          </P>
          <P>
            <strong>Zurückziehen</strong> macht den Link sofort unbrauchbar. Das ist der richtige
            Griff, wenn eine Einladung an die falsche Adresse ging.
          </P>
        </>
      ),
      en: (
        <>
          <P>
            An invitation creates a link valid for seven days. With no mail relay configured it is
            displayed here rather than sent — once, to be copied. A link that exists only in a log
            nobody reads would be worse than one you have to pass on yourself.
          </P>
          <P>
            You can only grant roles below your own: an administrator can invite editors and
            analysts, not further administrators.
          </P>
          <P>
            <strong>Revoke</strong> makes the link unusable immediately. That is the right handle
            when an invitation went to the wrong address.
          </P>
        </>
      ),
    },
  },

  'api-schluessel': {
    group: 'konto',
    title: { de: 'API-Schlüssel', en: 'API keys' },
    teaser: {
      de: 'Zugang für Skripte — lesend, befristet, und nur einmal sichtbar.',
      en: 'Access for scripts — read-only, time-limited, shown once.',
    },
    body: {
      de: (
        <>
          <P>
            Ein Schlüssel gehört dem Konto, nicht einer Person. Deshalb kann er lesen und
            auswerten, aber nie Zugänge verändern: ein Skript, das Kollegen einladen kann, ist ein
            Skript, mit dem man ein Konto übernimmt.
          </P>
          <P>
            Der Schlüssel wird bei der Erstellung genau einmal angezeigt. Danach existiert nur noch
            sein Hash — was ihn davor schützt, aus einer Datenbanksicherung ausgelesen zu werden,
            und bedeutet, dass ein verlorener Schlüssel ersetzt und nicht wiedergefunden wird.
          </P>
          <Note>
            <strong>Widerrufen</strong> ist endgültig: jede Integration, die ihn benutzt, bekommt ab
            sofort <C>401</C>. Der Name in der Liste ist das Einzige, woran sich später erkennen
            lässt, welches Skript stehen bleibt — „Reporting-Skript“ hilft, „Test“ nicht.
          </Note>
        </>
      ),
      en: (
        <>
          <P>
            A key belongs to the account, not to a person. That is why it can read and analyse but
            never change access: a script that can invite colleagues is a script you can take over
            an account with.
          </P>
          <P>
            The key is displayed exactly once, at creation. After that only its hash exists — which
            is what stops it being read out of a database backup, and means a lost key is replaced
            rather than recovered.
          </P>
          <Note>
            <strong>Revoking</strong> is final: every integration using it gets <C>401</C> from that
            moment. The name in the list is the only thing that later tells you which script you
            are about to break — "reporting script" helps, "test" does not.
          </Note>
        </>
      ),
    },
  },
} as const satisfies Record<string, Topic>;
